import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, FilterIcon, GridIcon, ListIcon, UsersIcon, PencilIcon, PrinterIcon, MessageSquareIcon, FileTextIcon, BookOpenIcon, MailIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../../components/UI/FilterSidebar';
import { ProfileCard } from '../../components/Directory/ProfileCard';
import { mapServiceToProfileCard } from '../../utils/profileCardMappers';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
// Mock data for service providers
const serviceProviders = [{
  id: 1,
  name: "Emma's Editing Services",
  category: 'editing',
  subcategory: 'developmental',
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.9,
  reviewCount: 127,
  location: 'New York, NY',
  remote: true,
  bio: 'Specializing in developmental editing for romance and fantasy novels with 10+ years of experience.',
  tags: ['Romance', 'Fantasy', 'YA'],
  featured: true,
  availability: 'Available',
  startingRate: 0.08,
  rateUnit: 'per word',
  contentBoundaries: ['No erotica', 'No extreme violence', 'No horror']
}, {
  id: 2,
  name: 'Clear Copy Editing',
  category: 'editing',
  subcategory: 'copyediting',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.7,
  reviewCount: 89,
  location: 'Chicago, IL',
  remote: true,
  bio: 'Meticulous copy editor with a passion for perfecting grammar, syntax, and style across all genres.',
  tags: ['Non-Fiction', 'Mystery', 'Academic'],
  featured: false,
  availability: 'Limited',
  startingRate: 0.04,
  rateUnit: 'per word',
  contentBoundaries: ['No political content', 'No religious debates']
}, {
  id: 3,
  name: 'ProofPerfect',
  category: 'editing',
  subcategory: 'proofreading',
  image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.8,
  reviewCount: 103,
  location: 'Seattle, WA',
  remote: true,
  bio: 'Final-pass proofreading to catch those last-minute errors before publication.',
  tags: ['Fiction', 'Non-Fiction', 'Academic'],
  featured: false,
  availability: 'Booked',
  startingRate: 0.02,
  rateUnit: 'per word',
  contentBoundaries: ['No explicit sexual content']
}, {
  id: 4,
  name: 'Pristine Productions',
  category: 'production',
  subcategory: 'formatting',
  image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.6,
  reviewCount: 78,
  location: 'Los Angeles, CA',
  remote: true,
  bio: 'Expert ebook and print formatting to make your book look professional inside and out.',
  tags: ['Ebook', 'Print', 'Fixed Layout'],
  featured: true,
  availability: 'Available',
  startingRate: 150,
  rateUnit: 'flat fee',
  contentBoundaries: ['No adult content']
}, {
  id: 5,
  name: 'Audiobook Artisans',
  category: 'production',
  subcategory: 'audio',
  image: 'https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.9,
  reviewCount: 64,
  location: 'Austin, TX',
  remote: true,
  bio: 'Full-service audiobook production from casting to final mastering.',
  tags: ['Fiction', 'Non-Fiction', 'Multi-cast'],
  featured: false,
  availability: 'Limited',
  startingRate: 300,
  rateUnit: 'per finished hour',
  contentBoundaries: ['No extreme violence', 'No explicit content']
}, {
  id: 6,
  name: 'Marketing Mavericks',
  category: 'support',
  subcategory: 'marketing',
  image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.8,
  reviewCount: 92,
  location: 'Miami, FL',
  remote: true,
  bio: 'Strategic book marketing to help your title stand out in a crowded marketplace.',
  tags: ['Social Media', 'Email', 'Advertising'],
  featured: true,
  availability: 'Available',
  startingRate: 500,
  rateUnit: 'package',
  contentBoundaries: ['No adult content', 'No political campaigns']
}, {
  id: 7,
  name: "Author's Assistant",
  category: 'support',
  subcategory: 'virtual-assistant',
  image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.7,
  reviewCount: 58,
  location: 'Denver, CO',
  remote: true,
  bio: "Virtual assistant services specifically tailored to authors' needs.",
  tags: ['Admin', 'Social Media', 'Research'],
  featured: false,
  availability: 'Limited',
  startingRate: 25,
  rateUnit: 'per hour',
  contentBoundaries: ['No explicit material research']
}, {
  id: 8,
  name: 'Beta Readers United',
  category: 'support',
  subcategory: 'beta-reading',
  image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.5,
  reviewCount: 73,
  location: 'Portland, OR',
  remote: true,
  bio: 'Organized beta reading services with detailed feedback reports.',
  tags: ['Romance', 'Fantasy', 'Mystery'],
  featured: false,
  availability: 'Available',
  startingRate: 0.01,
  rateUnit: 'per word',
  contentBoundaries: ['No horror', 'No extreme violence']
}, {
  id: 9,
  name: 'Manuscript Mentors',
  category: 'editing',
  subcategory: 'mentoring',
  image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.9,
  reviewCount: 42,
  location: 'Boston, MA',
  remote: true,
  bio: 'Personalized mentoring to help authors develop their craft and manuscripts.',
  tags: ['Fiction', 'Non-Fiction', 'Career'],
  featured: false,
  availability: 'Booked',
  startingRate: 75,
  rateUnit: 'per hour',
  contentBoundaries: ['No explicit content', 'No hate speech']
}, {
  id: 10,
  name: 'Print Perfect',
  category: 'production',
  subcategory: 'printing',
  image: 'https://images.unsplash.com/photo-1552058544-f2b08422138a?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.6,
  reviewCount: 87,
  location: 'San Francisco, CA',
  remote: false,
  bio: 'Quality print services for independent authors, including special editions.',
  tags: ['Hardcover', 'Paperback', 'Special Editions'],
  featured: true,
  availability: 'Available',
  startingRate: 200,
  rateUnit: 'package',
  contentBoundaries: ['No adult content', 'No graphic violence']
}, {
  id: 11,
  name: "Author's Web",
  category: 'support',
  subcategory: 'web-design',
  image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.8,
  reviewCount: 65,
  location: 'Atlanta, GA',
  remote: true,
  bio: 'Custom author websites that convert visitors to readers and fans.',
  tags: ['Web Design', 'SEO', 'Maintenance'],
  featured: false,
  availability: 'Limited',
  startingRate: 750,
  rateUnit: 'project',
  contentBoundaries: ['No adult content sites', 'No gambling content']
}, {
  id: 12,
  name: 'Publicity Partners',
  category: 'support',
  subcategory: 'publicity',
  image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.7,
  reviewCount: 54,
  location: 'Nashville, TN',
  remote: true,
  bio: 'Book publicity services to get your title in front of readers, reviewers, and media.',
  tags: ['Media Relations', 'Book Tours', 'Press'],
  featured: true,
  availability: 'Available',
  startingRate: 400,
  rateUnit: 'package',
  contentBoundaries: ['No explicit content', 'No political campaigns']
}];
// Category information
const categories = [{
  id: 'all',
  name: 'All Services',
  icon: <UsersIcon className="h-5 w-5" data-id="element-14294" data-mp="icn1owec3stn" />,
  count: serviceProviders.length
}, {
  id: 'editing',
  name: 'Editing Services',
  icon: <PencilIcon className="h-5 w-5" data-id="element-14295" data-mp="mf9xt9i1fzwn" />,
  count: serviceProviders.filter(p => p.category === 'editing').length,
  subcategories: [{
    id: 'developmental',
    name: 'Developmental Editing'
  }, {
    id: 'copyediting',
    name: 'Copyediting'
  }, {
    id: 'proofreading',
    name: 'Proofreading'
  }, {
    id: 'mentoring',
    name: 'Author Mentoring'
  }]
}, {
  id: 'production',
  name: 'Production Services',
  icon: <PrinterIcon className="h-5 w-5" data-id="element-14296" data-mp="3s8u8nzd8q3cl" />,
  count: serviceProviders.filter(p => p.category === 'production').length,
  subcategories: [{
    id: 'formatting',
    name: 'Book Formatting'
  }, {
    id: 'audio',
    name: 'Audio Production'
  }, {
    id: 'printing',
    name: 'Print Services'
  }]
}, {
  id: 'support',
  name: 'Support Services',
  icon: <MessageSquareIcon className="h-5 w-5" data-id="element-14297" data-mp="34k19zv58hhea" />,
  count: serviceProviders.filter(p => p.category === 'support').length,
  subcategories: [{
    id: 'marketing',
    name: 'Marketing'
  }, {
    id: 'virtual-assistant',
    name: 'Virtual Assistant'
  }, {
    id: 'beta-reading',
    name: 'Beta Reading'
  }, {
    id: 'web-design',
    name: 'Web Design'
  }, {
    id: 'publicity',
    name: 'Publicity'
  }]
}];
const ServiceDirectory = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filters, setFilters] = useState({
    remote: false,
    availability: [] as string[],
    minRating: 0
  });
  // Get the current category object
  const currentCategory = categories.find(c => c.id === selectedCategory);
  // Filter providers based on selected criteria
  const filteredProviders = serviceProviders.filter(provider => {
    // Filter by search query
    if (searchQuery && !provider.name.toLowerCase().includes(searchQuery.toLowerCase()) && !provider.bio.toLowerCase().includes(searchQuery.toLowerCase()) && !provider.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))) {
      return false;
    }
    // Filter by category
    if (selectedCategory !== 'all' && provider.category !== selectedCategory) {
      return false;
    }
    // Filter by subcategory
    if (selectedSubcategory && provider.subcategory !== selectedSubcategory) {
      return false;
    }
    // Filter by remote
    if (filters.remote && !provider.remote) {
      return false;
    }
    // Filter by availability
    if (filters.availability.length > 0 && !filters.availability.includes(provider.availability)) {
      return false;
    }
    // Filter by minimum rating
    if (provider.rating < filters.minRating) {
      return false;
    }
    return true;
  });
  const filterSections: FilterSection[] = [{
    id: 'category',
    title: 'Category',
    type: 'radio',
    options: categories.map(cat => ({
      value: cat.id,
      label: cat.name,
      count: cat.count
    })),
    value: selectedCategory,
    onChange: val => {
      setSelectedCategory(val);
      setSelectedSubcategory(null);
    }
  }, ...(selectedCategory !== 'all' && currentCategory?.subcategories ? [{
    id: 'subcategory',
    title: 'Subcategory',
    type: 'radio',
    options: currentCategory.subcategories.map(sub => ({
      value: sub.id,
      label: sub.name
    })),
    value: selectedSubcategory || '',
    onChange: val => setSelectedSubcategory(val)
  }] : []), {
    id: 'remote',
    title: 'Remote Only',
    type: 'checkbox',
    options: [{
      value: 'remote',
      label: 'Remote Only'
    }],
    value: filters.remote ? ['remote'] : [],
    onChange: () => setFilters({
      ...filters,
      remote: !filters.remote
    })
  }, {
    id: 'availability',
    title: 'Availability',
    type: 'checkbox',
    options: ['Available', 'Limited', 'Booked'].map(status => ({
      value: status,
      label: status
    })),
    value: filters.availability,
    onChange: val => {
      const newAvailability = filters.availability.includes(val) ? filters.availability.filter(s => s !== val) : [...filters.availability, val];
      setFilters({
        ...filters,
        availability: newAvailability
      });
    }
  }, {
    id: 'rating',
    title: 'Minimum Rating',
    type: 'radio',
    options: [{
      value: '0',
      label: 'Any Rating'
    }, {
      value: '3',
      label: '3+ Stars'
    }, {
      value: '4',
      label: '4+ Stars'
    }, {
      value: '4.5',
      label: '4.5+ Stars'
    }],
    value: filters.minRating.toString(),
    onChange: val => setFilters({
      ...filters,
      minRating: parseFloat(val)
    })
  }];
  return <div className={`min-h-screen w-full ${dk.page}`} data-id="element-14298" data-mp="33h0dqx89829v">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-14299" data-mp="33ijyb00y0zhg">
        {/* Page Header */}
        <div className="text-center mb-8" data-id="element-14300" data-mp="2ygulpfy2odr9">
          <h1 className={`text-3xl font-bold mb-2 ${dk.textHeading}`} data-id="element-14301" data-mp="3df6ehbibr2d5">
            Service Provider Directory
          </h1>
          <p className={`text-lg max-w-3xl mx-auto ${dk.textSecondary}`} data-id="element-14302" data-mp="38faedncqzv2v">
            Connect with professional editors, production specialists, and
            support services to help bring your book to life.
          </p>
        </div>

        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-6" data-id="element-14303" data-mp="dujoiaxx7">
          <button onClick={() => setSidebarOpen(true)} className={`w-full flex items-center justify-center px-4 py-3 border rounded-md ${dk.card}`} data-id="element-14304" data-mp="11oin1m1iee">
            <FilterIcon className="h-5 w-5 mr-2" data-id="element-14305" data-mp="eojy9zu1537o" />
            <span data-id="element-14306" data-mp="jmbo8ob46f98">Filters</span>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6" data-id="element-14307" data-mp="jltnbedibcuz">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-14308" data-mp="4o9lggbwpxt1">
            <FilterSidebar title="Filters" sections={filterSections} onReset={() => {
            setSelectedCategory('all');
            setSelectedSubcategory(null);
            setSearchQuery('');
            setFilters({
              remote: false,
              availability: [],
              minRating: 0
            });
          }} searchable={false} data-id="element-14309" data-mp="nh93e7qncsj" />

            {/* Quick Links */}
            {!sidebarCollapsed && <div className={`mt-8 pt-6 border-t ${dk.border}`} data-id="element-14310" data-mp="1jmfa4c4g0yoq">
                <h3 className={`font-semibold text-sm mb-4 uppercase tracking-wider ${dk.textSecondary}`} data-id="element-14311" data-mp="1evfbn2foe8k4">
                  Quick Links
                </h3>
                <div className="space-y-3" data-id="element-14312" data-mp="14x9a4lxs74fs">
                  {[{
                to: '/services/editing/opportunities',
                icon: FileTextIcon,
                label: 'Editing Opportunities'
              }, {
                to: '/services/production/opportunities',
                icon: FileTextIcon,
                label: 'Production Opportunities'
              }, {
                to: '/services/support/opportunities',
                icon: FileTextIcon,
                label: 'Support Opportunities'
              }, {
                to: '/services/editing/gallery',
                icon: BookOpenIcon,
                label: 'Editor Gallery'
              }, {
                to: '/services/submit',
                icon: MailIcon,
                label: 'Submit Your Services'
              }].map(({
                to,
                icon: Icon,
                label
              }) => <Link key={to} to={to} className={`flex items-center text-sm ${dk.textSecondary} hover:${rt.textPrimary} transition-colors`} data-id="element-14313" data-mp="37jwown5l8q27">
                      <Icon className="h-4 w-4 mr-2" data-id="element-14314" data-mp="3glnxq0vfdd0a" />
                      {label}
                    </Link>)}
                </div>
              </div>}
          </FilterSidebarWrapper>

          {/* Main Content */}
          <div className="flex-1 min-w-0" data-id="element-14315" data-mp="1m8y0pa9rfnoo">
            {/* Search and View Toggle */}
            <div className={`rounded-lg shadow-sm p-4 mb-6 border ${dk.card}`} data-id="element-14316" data-mp="1r7r429vc1yb4">
              <div className="flex flex-col md:flex-row gap-4 justify-between items-center" data-id="element-14317" data-mp="3t1njo7gj1p60">
                <div className="relative flex-grow w-full md:w-auto" data-id="element-14318" data-mp="3p0ttt8jiy0ah">
                  <SearchIcon className={`h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 ${dk.textFaint}`} data-id="element-14319" data-mp="6nvr4tvcktcm" />
                  <input type="text" placeholder="Search by name, skills, or keywords..." className={`w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-14320" data-mp="1qm5lpb2x1u2" />
                </div>
                <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end" data-id="element-14321" data-mp="zc1d1jy50oe1">
                  <div className="flex items-center" data-id="element-14322" data-mp="udgk85l7t6zm">
                    <span className={`text-sm mr-2 whitespace-nowrap ${dk.textSecondary}`} data-id="element-14323" data-mp="ucyjowris6lw">
                      Sort by:
                    </span>
                    <select className={`block pl-3 pr-10 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} data-id="element-14324" data-mp="2hbheb8exa4n0">
                      <option data-id="element-14325" data-mp="289hy2ibqf4l4">Highest Rated</option>
                      <option data-id="element-14326" data-mp="2vyapq2lbmq9x">Most Reviews</option>
                      <option data-id="element-14327" data-mp="2vzlzcj1g6s8y">A-Z</option>
                      <option data-id="element-14328" data-mp="300xkab9dmpat">Lowest Rate</option>
                    </select>
                  </div>
                  <div className={`flex rounded-md border overflow-hidden ${dk.border}`} data-id="element-14329" data-mp="2m9j1o5jdsq4a">
                    <button onClick={() => setViewMode('grid')} className={`flex items-center justify-center px-3 py-2 ${viewMode === 'grid' ? `text-white ${rt.bgDark}` : dk.card}`} data-id="element-14330" data-mp="2r6swy1tgbw01">
                      <GridIcon className="h-4 w-4" data-id="element-14331" data-mp="3si03vyrgatl" />
                    </button>
                    <button onClick={() => setViewMode('list')} className={`flex items-center justify-center px-3 py-2 ${viewMode === 'list' ? `text-white ${rt.bgDark}` : dk.card}`} data-id="element-14332" data-mp="3ukyb3a2ha9e">
                      <ListIcon className="h-4 w-4" data-id="element-14333" data-mp="3v6e42l5zrb6c" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Results */}
            {filteredProviders.length > 0 ? <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6' : 'space-y-4'} aria-label="Service provider directory" data-id="element-14334" data-mp="3m7sj6jd1rk2e">
                {filteredProviders.map(provider => <ProfileCard key={provider.id} data={mapServiceToProfileCard(provider)} viewMode={viewMode} data-id="element-14335" data-mp="1ah839drthtme" />)}
              </div> : <div className={`rounded-lg shadow-sm p-8 text-center border ${dk.card}`} data-id="element-14336" data-mp="2qvayvcilc9rg">
                <div className={`inline-block p-4 rounded-full mb-4 ${dk.surfaceMuted}`} data-id="element-14337" data-mp="2zy2d1k2upjms">
                  <SearchIcon className={`h-8 w-8 ${dk.textFaint}`} data-id="element-14338" data-mp="3em6ygvtag999" />
                </div>
                <h3 className={`text-lg font-medium mb-1 ${dk.textHeading}`} data-id="element-14339" data-mp="39oew17v0p4qt">
                  No service providers found
                </h3>
                <p className={`mb-4 ${dk.textMuted}`} data-id="element-14340" data-mp="3nmvc3pers7lm">
                  Try adjusting your filters or search terms to find what you're
                  looking for.
                </p>
                <button onClick={() => {
              setSelectedCategory('all');
              setSelectedSubcategory(null);
              setSearchQuery('');
              setFilters({
                remote: false,
                availability: [],
                minRating: 0
              });
            }} className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${rt.primaryButton}`} data-id="element-14341" data-mp="1taf007zalz8u">
                  Reset All Filters
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default ServiceDirectory;